import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Button,
  Tabs,
  Tab,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Menu,
  Divider,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Popover,
  IconButton,
  CircularProgress,
  Modal,
  Slide,
  // --- PAGINATION: Import required components ---
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
// --- PAGINATION: Import required icons ---
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { gql, useQuery } from '@apollo/client';
import TraceDetailPanel from './TraceDetailPanel';


// --- GQL QUERIES ---
const GET_LLM_DATA_SOURCES = gql`
  query GetLlmDataSourcesByTenant($tenantId: ID!) {
    allDataSourcesByTenant(tenantId: $tenantId) {
      __typename
      ... on LlmDataSource {
        id
        providerType
      }
    }
  }
`;

const GET_LLM_TRACES_FILTER_OPTIONS = gql`
  query GetLlmTracesFilterOptions($tenantId: ID!, $llmDataSourceIds: [ID!]) {
    getLlmTracesFilterOptions(tenantId: $tenantId, llmDataSourceIds: $llmDataSourceIds) {
      models
    }
  }
`;

const GET_LLM_TRACES = gql`
  query GetLlmTraces($tenantId: ID!, $llmDataSourceIds: [ID!], $filters: LlmTracesFiltersInput) {
    getLlmTraces(
      tenantId: $tenantId
      llmDataSourceIds: $llmDataSourceIds
      filters: $filters
    ) {
      totalCount
      traces {
        id
        timestamp
        name
        userId
        latency
        cost
        totalTokens
        inputTokens
        outputTokens
      }
    }
  }
`;

// --- INTERFACES ---
interface TraceDetailsProps {
  open: boolean;
  onClose: () => void;
  tenantId?: string;
}
interface Trace {
  id: string;
  timestamp: string;
  name: string;
  latency: number;
  cost: number | null;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
}

const TraceDetails: React.FC<TraceDetailsProps> = ({ open, onClose, tenantId }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);

  // --- MODEL/TAB STATE ---
  const [models, setModels] = useState<string[]>(['All']);
  const [selectedModel, setSelectedModel] = useState<string | null>('All');

  // --- PAGINATION: Add state for page number and rows per page ---
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  const {
    data: llmSourcesData,
    loading: llmSourcesLoading,
    error: llmSourcesError
  } = useQuery(GET_LLM_DATA_SOURCES, {
    variables: { tenantId: tenantId },
    skip: !tenantId || !open,
  });

  const llmDataSourceIds = useMemo(() => {
    if (!llmSourcesData?.allDataSourcesByTenant) return [];
    return llmSourcesData.allDataSourcesByTenant
      .filter((ds: any) => ds.__typename === 'LlmDataSource')
      .map((ds: any) => ds.id);
  }, [llmSourcesData]);
  
  const {
    data: filterOptionsData,
    loading: filterOptionsLoading,
    error: filterOptionsError,
  } = useQuery(GET_LLM_TRACES_FILTER_OPTIONS, {
      variables: { tenantId, llmDataSourceIds },
      skip: !tenantId || llmDataSourceIds.length === 0 || !open,
  });

  useEffect(() => {
    if (filterOptionsData?.getLlmTracesFilterOptions?.models) {
        const fetchedModels = filterOptionsData.getLlmTracesFilterOptions.models;
        setModels(['All', ...fetchedModels]);
        // Default selection is already 'All', activeTab is 0
    }
  }, [filterOptionsData]);


  const { loading, error, data } = useQuery(GET_LLM_TRACES, {
    variables: { 
        tenantId: tenantId, 
        llmDataSourceIds: llmDataSourceIds,
        filters: {
            models: selectedModel === 'All' ? null : (selectedModel ? [selectedModel] : null)
        }
    },
    skip: !tenantId || llmDataSourceIds.length === 0 || !open || !selectedModel,
  });

  const traces: Trace[] = useMemo(() => data?.getLlmTraces?.traces || [], [data]);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if(models && models[newValue]){
        setSelectedModel(models[newValue]);
    }
  };

  const handleRowClick = (trace: Trace) => setSelectedTrace(trace);
  const handleDetailPanelClose = () => setSelectedTrace(null);

  const [tokenPopoverAnchorEl, setTokenPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [tokenPopoverData, setTokenPopoverData] = useState<{ input: number; output: number } | null>(null);
  const handleTokenPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setTokenPopoverData({ input: trace.inputTokens, output: trace.outputTokens }); setTokenPopoverAnchorEl(event.currentTarget); };
  const handleTokenPopoverClose = () => setTokenPopoverAnchorEl(null);
  const isTokenPopoverOpen = Boolean(tokenPopoverAnchorEl);

  const [costPopoverAnchorEl, setCostPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [costPopoverData, setCostPopoverData] = useState<{ input: number; output: number } | null>(null);
  const handleCostPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setCostPopoverData({ input: trace.cost || 0, output: 0 }); setCostPopoverAnchorEl(event.currentTarget); };
  const handleCostPopoverClose = () => setCostPopoverAnchorEl(null);
  const isCostPopoverOpen = Boolean(costPopoverAnchorEl);

  const [traceNameAnchorEl, setTraceNameAnchorEl] = useState<null | HTMLElement>(null);
  const [timeRangeAnchorEl, setTimeRangeAnchorEl] = useState<null | HTMLElement>(null);
  const isTraceNameMenuOpen = Boolean(traceNameAnchorEl);
  const isTimeRangeMenuOpen = Boolean(timeRangeAnchorEl);
  const [selectedTraceNames, setSelectedTraceNames] = useState<string[]>([]);
  const [tempTraceNames, setTempTraceNames] = useState<string[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('');
  const [tempTimeRange, setTempTimeRange] = useState('');

  const traceNames = useMemo(() => {
    if (!traces) return [];
    const names = traces.map(trace => trace.name);
    return Array.from(new Set(names));
  }, [traces]);

  const timeRanges = ['Last 24 Hours', 'Last 7 Days', 'Last 30 Days'];

  const filteredTraces = useMemo(() => {
    let result: Trace[] = [...traces];
    if (selectedTraceNames.length > 0) {
      result = result.filter(trace => selectedTraceNames.includes(trace.name));
    }
    if (selectedTimeRange) {
      const now = new Date();
      let cutoffDate: Date | null = null;
      if (selectedTimeRange === 'Last 24 Hours') {
        cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      } else if (selectedTimeRange === 'Last 7 Days') {
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (selectedTimeRange === 'Last 30 Days') {
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }
      if (cutoffDate) {
        result = result.filter(trace => new Date(trace.timestamp) >= (cutoffDate as Date));
      }
    }
    if (searchQuery) {
      result = result.filter(trace =>
        trace.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return result;
  }, [traces, selectedTraceNames, selectedTimeRange, searchQuery]);
  
  // --- PAGINATION: Add effect to reset page when filters change ---
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedTraceNames, selectedTimeRange, selectedModel]);

  // --- PAGINATION: Memoize the sliced data for the current page ---
  const paginatedTraces = useMemo(() => {
      const startIndex = (page - 1) * rowsPerPage;
      return filteredTraces.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredTraces, page, rowsPerPage]);


  const handleTraceNameMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTraceNames(selectedTraceNames); setTraceNameAnchorEl(event.currentTarget); };
  const handleTraceNameMenuClose = () => setTraceNameAnchorEl(null);
  const handleTraceNameCheckboxChange = (name: string) => { setTempTraceNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]); };
  const handleApplyTraceNames = () => { setSelectedTraceNames(tempTraceNames); handleTraceNameMenuClose(); };
  const handleClearTraceNames = () => { setTempTraceNames([]); setSelectedTraceNames([]); handleTraceNameMenuClose(); };
  const getTraceNameButtonText = () => {
    const selectedCount = selectedTraceNames.length;
    const totalCount = traceNames.length;

    if (selectedCount === 0) return 'Trace Name';
    if (selectedCount === totalCount && totalCount > 0) return 'All';
    return `${selectedCount} selected`;
  };

  const handleTimeRangeMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTimeRange(selectedTimeRange); setTimeRangeAnchorEl(event.currentTarget); };
  const handleTimeRangeMenuClose = () => setTimeRangeAnchorEl(null);
  const handleApplyTimeRange = () => { setSelectedTimeRange(tempTimeRange); handleTimeRangeMenuClose(); };
  const handleClearTimeRange = () => { setTempTimeRange(''); setSelectedTimeRange(''); handleTimeRangeMenuClose(); };

  const formatTimestamp = (isoString: string) => { const date = new Date(isoString); const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }; const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true }; return `${date.toLocaleDateString('en-GB', dateOptions)} • ${date.toLocaleTimeString('en-US', timeOptions)}`; };
  
  // --- PAGINATION: Handler for changing rows per page ---
  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
      setRowsPerPage(event.target.value as number);
      setPage(1);
  };


  const renderContent = () => {
    if (llmSourcesLoading || filterOptionsLoading || (loading && llmDataSourceIds.length > 0)) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        );
    }
    if (llmSourcesError) {
      console.error("Error fetching LLM data sources:", JSON.stringify(llmSourcesError, null, 2));
      return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error: {llmSourcesError.message}</Typography>;
    }
    if (filterOptionsError) {
        console.error("Error fetching filter options:", JSON.stringify(filterOptionsError, null, 2));
        return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error fetching filter options: {filterOptionsError.message}</Typography>;
    }
    if (error) {
      console.error("Error fetching LLM traces:", JSON.stringify(error, null, 2));
      return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error fetching traces: {error.message}</Typography>;
    }
    if (!tenantId) {
        return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>Waiting for tenant information...</Typography>;
    }
    if (llmDataSourceIds.length === 0 && !llmSourcesLoading) {
      return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>No LLM data sources found for this tenant.</Typography>;
    }
    if (models.length <= 1 && !filterOptionsLoading) {
      return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>No models found for the available data sources.</Typography>;
    }

    // --- PAGINATION: Calculate values for the display text ---
    const pageCount = Math.ceil(filteredTraces.length / rowsPerPage);
    const showingFrom = filteredTraces.length > 0 ? (page - 1) * rowsPerPage + 1 : 0;
    const showingTo = Math.min(page * rowsPerPage, filteredTraces.length);

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 'auto', '& .MuiTabs-indicator': { backgroundColor: '#0064FF' } }}>
                {models.map((model, index) => (
                    <Tab 
                        key={model}
                        label={model} 
                        sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === index ? '#0064FF' : '#667085', backgroundColor: activeTab === index ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} 
                    />
                ))}
              </Tabs>
              <Paper elevation={0} sx={{ p: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderRadius: '12px', bgcolor: 'white', width: '100%',ml:-2 }}>
                  <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#232324', fontWeight: '400', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
                  <Button id="trace-name-filter-button" aria-haspopup="true" variant="contained" onClick={handleTraceNameMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTraceNameMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#0064FF' : theme.palette.divider, backgroundColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
                      <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{getTraceNameButtonText()}</Typography>
                  </Button>
                  <Menu id="trace-name-menu" anchorEl={traceNameAnchorEl} open={isTraceNameMenuOpen} onClose={handleTraceNameMenuClose} MenuListProps={{ 'aria-labelledby': 'trace-name-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
                    <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Trace Name</Typography></Box> <Divider /> <Box sx={{ py: '10px' }}><FormGroup>{traceNames.map(name => (<FormControlLabel key={name} control={ <Checkbox checked={tempTraceNames.includes(name)} onChange={() => handleTraceNameCheckboxChange(name)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } label={ <Box sx={{ display: 'flex', alignItems: 'center' }}><Typography sx={{fontFamily:'Inter', fontSize:'14px'}}>{name}</Typography></Box> } sx={{ width: '100%', m: 0, px: '1px',pl:'6px', py: '4px', ...(tempTraceNames.includes(name) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTraceNames.includes(name) ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</FormGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: tempTraceNames.length > 0 ? '#333333' : '#7B7B7B', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
                  </Menu>
                  <Button id="time-range-filter-button" aria-haspopup="true" variant="contained" onClick={handleTimeRangeMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTimeRangeMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTimeRangeMenuOpen || selectedTimeRange ? '#0064FF' : theme.palette.divider, backgroundColor: isTimeRangeMenuOpen || selectedTimeRange ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
                      <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{selectedTimeRange || 'Time Range'}</Typography>
                  </Button>
                  <Menu id="time-range-menu" anchorEl={timeRangeAnchorEl} open={isTimeRangeMenuOpen} onClose={handleTimeRangeMenuClose} MenuListProps={{ 'aria-labelledby': 'time-range-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
                    <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Time Range</Typography></Box> <Divider /> <Box sx={{ py: '8px' }}><RadioGroup value={tempTimeRange} onChange={(e) => setTempTimeRange(e.target.value)}>{timeRanges.map(range => (<FormControlLabel key={range} value={range} control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={<Typography sx={{ fontFamily:'Inter', fontSize:'14px' }}>{range}</Typography>} sx={{ width: '100%', m: 0, px: '1px', py: '4px', ...(tempTimeRange === range && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTimeRange === range ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</RadioGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: !tempTimeRange ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
                  </Menu>
              </Paper>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px',ml:-1 }}>
              <Table sx={{ minWidth: 700 }} aria-label="traces table">
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', backgroundColor: 'white', py: 1.5, px: 3, fontFamily: 'Inter', fontSize: '12px', color: '#667085', fontWeight: 500 } }}>
                    <TableCell>Name</TableCell><TableCell>Timestamp</TableCell><TableCell>Input</TableCell><TableCell>Output</TableCell><TableCell>Latency</TableCell><TableCell>Tokens</TableCell><TableCell>Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* --- PAGINATION: Map over paginated data and handle empty state --- */}
                  {paginatedTraces.length > 0 ? (
                    paginatedTraces.map((trace, index) => (
                      <TableRow key={trace.id} onClick={() => handleRowClick(trace)} sx={{ '& td, & th': { border: 0, py: 2.5, px: 3, fontFamily: 'Inter' }, backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'white', cursor: 'pointer', '&:hover': { backgroundColor: '#F0F2F5' }}}>
                        <TableCell component="th" scope="row"><Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828' }}>{trace.name}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{formatTimestamp(trace.timestamp)}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.latency.toFixed(2)}s</Typography></TableCell>
                        <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.totalTokens}</Typography><IconButton size="small" onMouseEnter={(e) => handleTokenPopoverOpen(e, trace)} onMouseLeave={handleTokenPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton></Box></TableCell>
                        <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.cost !== null ? `$${trace.cost.toFixed(6)}` : '-'}</Typography>{trace.cost !== null && (<IconButton size="small" onMouseEnter={(e) => handleCostPopoverOpen(e, trace)} onMouseLeave={handleCostPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton>)}</Box></TableCell>
                      </TableRow>
                    ))
                   ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, fontFamily: 'Inter', border: 0 }}>
                          <Typography>No traces match the current filters.</Typography>
                      </TableCell>
                    </TableRow>
                   )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* --- PAGINATION: Add the pagination controls UI --- */}
            {filteredTraces.length > 0 && (
                <Box sx={{ flexShrink: 0, mt: 1, mb: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, minHeight: '3.5rem', padding: '0 1.5rem' }}>
                    <Typography sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary' }}>Rows per page:</Typography>
                    <FormControl size="small">
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary', '.MuiOutlinedInput-notchedOutline': { border: 'none' }, '.MuiSelect-select': { padding: '4px 8px' }, }}>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
                        {showingFrom} - {showingTo} of {filteredTraces.length}
                    </Typography>
                    <IconButton onClick={() => setPage(page - 1)} disabled={page === 1} aria-label="previous page">
                        <ArrowBackIosNewIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <IconButton onClick={() => setPage(page + 1)} disabled={page === pageCount} aria-label="next page">
                        <ArrowForwardIosIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Box>
            )}
        </>
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose} closeAfterTransition>
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
            <Box
                sx={{
                position: 'absolute',
                top: 60, 
                bottom: 15, 
                left: 15, 
                right: 15,
                bgcolor: 'background.paper',
                borderRadius: '24px',
                boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                outline: 0,
            }}>
                <Box sx={{
                    pt: 0,
                    px: '24px',
                    pb: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #EAECEE',
                    backgroundImage: 'url(/textures/Texture.svg), url(/images/Gradient_Popup_long.svg)',
                    backgroundRepeat: 'no-repeat, no-repeat',
                    backgroundPosition: 'top right, top right',
                    backgroundSize: 'contain, contain'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '24px' }}>
                        <Box sx={{ width: 45, height: 45, bgcolor: ' #E5EFFF', borderRadius: '10%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src="/icons/llm-observability-blue.svg" alt="Traces Logo" style={{ width: 24, height: 24 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '18px',color:"#333333" }}>
                                Traces
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Inter',fontSize: '12px',fontWeight: 400, }}>
                                Track details of every trace in here
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} aria-label="close" sx={{ color: '#667085', mt: '16px' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '24px', bgcolor: '#FFFFFF', position: 'relative' }}>
                   {renderContent()}
                </Box>

                <TraceDetailPanel
                  open={!!selectedTrace}
                  onClose={handleDetailPanelClose}
                  tenantId={tenantId}
                  traceId={selectedTrace?.id}
                />
            </Box>
        </Slide>
      </Modal>

      {/* --- POPOVERS --- */}
      <Popover open={isTokenPopoverOpen} anchorEl={tokenPopoverAnchorEl} onClose={handleTokenPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
        <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Token Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Input Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.input} </Typography> </Box> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Output Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.output} </Typography> </Box> </Box>
      </Popover>

      <Popover open={isCostPopoverOpen} anchorEl={costPopoverAnchorEl} onClose={handleCostPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
        <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Cost Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Total Cost Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> ${costPopoverData?.input.toFixed(6)} </Typography> </Box> </Box>
      </Popover>
    </>
  );
};

export default TraceDetails;





// import React, { useMemo, useState, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   useTheme,
//   Button,
//   Tabs,
//   Tab,
//   TableContainer,
//   Paper,
//   Table,
//   TableHead,
//   TableRow,
//   TableCell,
//   TableBody,
//   InputAdornment,
//   Menu,
//   Divider,
//   TextField,
//   RadioGroup,
//   Radio,
//   FormControlLabel,
//   FormGroup,
//   Checkbox,
//   Popover,
//   IconButton,
//   CircularProgress,
//   Modal,
//   Slide,
//   // --- PAGINATION: Import required components ---
//   FormControl,
//   Select,
//   MenuItem,
//   SelectChangeEvent,
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import CloseIcon from '@mui/icons-material/Close';
// // --- PAGINATION: Import required icons ---
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// import { gql, useQuery } from '@apollo/client';
// import TraceDetailPanel from './TraceDetailPanel';


// // --- GQL QUERIES ---
// const GET_LLM_DATA_SOURCES = gql`
//   query GetLlmDataSourcesByTenant($tenantId: ID!) {
//     allDataSourcesByTenant(tenantId: $tenantId) {
//       __typename
//       ... on LlmDataSource {
//         id
//         providerType
//       }
//     }
//   }
// `;

// const GET_LLM_TRACES_FILTER_OPTIONS = gql`
//   query GetLlmTracesFilterOptions($tenantId: ID!, $llmDataSourceIds: [ID!]) {
//     getLlmTracesFilterOptions(tenantId: $tenantId, llmDataSourceIds: $llmDataSourceIds) {
//       models
//     }
//   }
// `;

// const GET_LLM_TRACES = gql`
//   query GetLlmTraces($tenantId: ID!, $llmDataSourceIds: [ID!], $filters: LlmTracesFiltersInput) {
//     getLlmTraces(
//       tenantId: $tenantId
//       llmDataSourceIds: $llmDataSourceIds
//       filters: $filters
//     ) {
//       totalCount
//       traces {
//         id
//         timestamp
//         name
//         userId
//         latency
//         cost
//         totalTokens
//         inputTokens
//         outputTokens
//       }
//     }
//   }
// `;

// // --- INTERFACES ---
// interface TraceDetailsProps {
//   open: boolean;
//   onClose: () => void;
//   tenantId?: string;
// }
// interface Trace {
//   id: string;
//   timestamp: string;
//   name: string;
//   latency: number;
//   cost: number | null;
//   totalTokens: number;
//   inputTokens: number;
//   outputTokens: number;
// }

// const TraceDetails: React.FC<TraceDetailsProps> = ({ open, onClose, tenantId }) => {
//   const theme = useTheme();
//   const [activeTab, setActiveTab] = useState(0);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);

//   // --- MODEL/TAB STATE ---
//   const [models, setModels] = useState<string[]>([]);
//   const [selectedModel, setSelectedModel] = useState<string | null>(null);

//   // --- PAGINATION: Add state for page number and rows per page ---
//   const [page, setPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(10);


//   const {
//     data: llmSourcesData,
//     loading: llmSourcesLoading,
//     error: llmSourcesError
//   } = useQuery(GET_LLM_DATA_SOURCES, {
//     variables: { tenantId: tenantId },
//     skip: !tenantId || !open,
//   });

//   const llmDataSourceIds = useMemo(() => {
//     if (!llmSourcesData?.allDataSourcesByTenant) return [];
//     return llmSourcesData.allDataSourcesByTenant
//       .filter((ds: any) => ds.__typename === 'LlmDataSource')
//       .map((ds: any) => ds.id);
//   }, [llmSourcesData]);
  
//   const {
//     data: filterOptionsData,
//     loading: filterOptionsLoading,
//     error: filterOptionsError,
//   } = useQuery(GET_LLM_TRACES_FILTER_OPTIONS, {
//       variables: { tenantId, llmDataSourceIds },
//       skip: !tenantId || llmDataSourceIds.length === 0 || !open,
//   });

//   useEffect(() => {
//     if (filterOptionsData?.getLlmTracesFilterOptions?.models) {
//         const fetchedModels = filterOptionsData.getLlmTracesFilterOptions.models;
//         setModels(fetchedModels);
//         if (fetchedModels.length > 0) {
//             setSelectedModel(fetchedModels[0]);
//             setActiveTab(0);
//         } else {
//             setSelectedModel(null);
//             setActiveTab(0);
//         }
//     }
//   }, [filterOptionsData]);


//   const { loading, error, data } = useQuery(GET_LLM_TRACES, {
//     variables: { 
//         tenantId: tenantId, 
//         llmDataSourceIds: llmDataSourceIds,
//         filters: {
//             models: selectedModel ? [selectedModel] : null
//         }
//     },
//     skip: !tenantId || llmDataSourceIds.length === 0 || !open || !selectedModel,
//   });

//   const traces: Trace[] = useMemo(() => data?.getLlmTraces?.traces || [], [data]);
//   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
//     setActiveTab(newValue);
//     if(models && models[newValue]){
//         setSelectedModel(models[newValue]);
//     }
//   };

//   const handleRowClick = (trace: Trace) => setSelectedTrace(trace);
//   const handleDetailPanelClose = () => setSelectedTrace(null);

//   const [tokenPopoverAnchorEl, setTokenPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [tokenPopoverData, setTokenPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleTokenPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setTokenPopoverData({ input: trace.inputTokens, output: trace.outputTokens }); setTokenPopoverAnchorEl(event.currentTarget); };
//   const handleTokenPopoverClose = () => setTokenPopoverAnchorEl(null);
//   const isTokenPopoverOpen = Boolean(tokenPopoverAnchorEl);

//   const [costPopoverAnchorEl, setCostPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [costPopoverData, setCostPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleCostPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setCostPopoverData({ input: trace.cost || 0, output: 0 }); setCostPopoverAnchorEl(event.currentTarget); };
//   const handleCostPopoverClose = () => setCostPopoverAnchorEl(null);
//   const isCostPopoverOpen = Boolean(costPopoverAnchorEl);

//   const [traceNameAnchorEl, setTraceNameAnchorEl] = useState<null | HTMLElement>(null);
//   const [timeRangeAnchorEl, setTimeRangeAnchorEl] = useState<null | HTMLElement>(null);
//   const isTraceNameMenuOpen = Boolean(traceNameAnchorEl);
//   const isTimeRangeMenuOpen = Boolean(timeRangeAnchorEl);
//   const [selectedTraceNames, setSelectedTraceNames] = useState<string[]>([]);
//   const [tempTraceNames, setTempTraceNames] = useState<string[]>([]);
//   const [selectedTimeRange, setSelectedTimeRange] = useState('');
//   const [tempTimeRange, setTempTimeRange] = useState('');

//   const traceNames = useMemo(() => {
//     if (!traces) return [];
//     const names = traces.map(trace => trace.name);
//     return Array.from(new Set(names));
//   }, [traces]);

//   const timeRanges = ['Last 24 Hours', 'Last 7 Days', 'Last 30 Days'];

//   const filteredTraces = useMemo(() => {
//     let result: Trace[] = [...traces];
//     if (selectedTraceNames.length > 0) {
//       result = result.filter(trace => selectedTraceNames.includes(trace.name));
//     }
//     if (selectedTimeRange) {
//       const now = new Date();
//       let cutoffDate: Date | null = null;
//       if (selectedTimeRange === 'Last 24 Hours') {
//         cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
//       } else if (selectedTimeRange === 'Last 7 Days') {
//         cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
//       } else if (selectedTimeRange === 'Last 30 Days') {
//         cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
//       }
//       if (cutoffDate) {
//         result = result.filter(trace => new Date(trace.timestamp) >= (cutoffDate as Date));
//       }
//     }
//     if (searchQuery) {
//       result = result.filter(trace =>
//         trace.name.toLowerCase().includes(searchQuery.toLowerCase())
//       );
//     }
//     return result;
//   }, [traces, selectedTraceNames, selectedTimeRange, searchQuery]);
  
//   // --- PAGINATION: Add effect to reset page when filters change ---
//   useEffect(() => {
//     setPage(1);
//   }, [searchQuery, selectedTraceNames, selectedTimeRange, selectedModel]);

//   // --- PAGINATION: Memoize the sliced data for the current page ---
//   const paginatedTraces = useMemo(() => {
//       const startIndex = (page - 1) * rowsPerPage;
//       return filteredTraces.slice(startIndex, startIndex + rowsPerPage);
//   }, [filteredTraces, page, rowsPerPage]);


//   const handleTraceNameMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTraceNames(selectedTraceNames); setTraceNameAnchorEl(event.currentTarget); };
//   const handleTraceNameMenuClose = () => setTraceNameAnchorEl(null);
//   const handleTraceNameCheckboxChange = (name: string) => { setTempTraceNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]); };
//   const handleApplyTraceNames = () => { setSelectedTraceNames(tempTraceNames); handleTraceNameMenuClose(); };
//   const handleClearTraceNames = () => { setTempTraceNames([]); setSelectedTraceNames([]); handleTraceNameMenuClose(); };
//   const getTraceNameButtonText = () => {
//     const selectedCount = selectedTraceNames.length;
//     const totalCount = traceNames.length;

//     if (selectedCount === 0) return 'Trace Name';
//     if (selectedCount === totalCount && totalCount > 0) return 'All';
//     return `${selectedCount} selected`;
//   };

//   const handleTimeRangeMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTimeRange(selectedTimeRange); setTimeRangeAnchorEl(event.currentTarget); };
//   const handleTimeRangeMenuClose = () => setTimeRangeAnchorEl(null);
//   const handleApplyTimeRange = () => { setSelectedTimeRange(tempTimeRange); handleTimeRangeMenuClose(); };
//   const handleClearTimeRange = () => { setTempTimeRange(''); setSelectedTimeRange(''); handleTimeRangeMenuClose(); };

//   const formatTimestamp = (isoString: string) => { const date = new Date(isoString); const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }; const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true }; return `${date.toLocaleDateString('en-GB', dateOptions)} • ${date.toLocaleTimeString('en-US', timeOptions)}`; };
  
//   // --- PAGINATION: Handler for changing rows per page ---
//   const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
//       setRowsPerPage(event.target.value as number);
//       setPage(1);
//   };


//   const renderContent = () => {
//     if (llmSourcesLoading || filterOptionsLoading || (loading && llmDataSourceIds.length > 0)) {
//         return (
//           <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//             <CircularProgress />
//           </Box>
//         );
//     }
//     if (llmSourcesError) {
//       console.error("Error fetching LLM data sources:", JSON.stringify(llmSourcesError, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error: {llmSourcesError.message}</Typography>;
//     }
//     if (filterOptionsError) {
//         console.error("Error fetching filter options:", JSON.stringify(filterOptionsError, null, 2));
//         return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error fetching filter options: {filterOptionsError.message}</Typography>;
//     }
//     if (error) {
//       console.error("Error fetching LLM traces:", JSON.stringify(error, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error fetching traces: {error.message}</Typography>;
//     }
//     if (!tenantId) {
//         return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>Waiting for tenant information...</Typography>;
//     }
//     if (llmDataSourceIds.length === 0 && !llmSourcesLoading) {
//       return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>No LLM data sources found for this tenant.</Typography>;
//     }
//     if (models.length === 0 && !filterOptionsLoading) {
//       return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>No models found for the available data sources.</Typography>;
//     }

//     // --- PAGINATION: Calculate values for the display text ---
//     const pageCount = Math.ceil(filteredTraces.length / rowsPerPage);
//     const showingFrom = filteredTraces.length > 0 ? (page - 1) * rowsPerPage + 1 : 0;
//     const showingTo = Math.min(page * rowsPerPage, filteredTraces.length);

//     return (
//         <>
//             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 3 }}>
//               <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 'auto', '& .MuiTabs-indicator': { backgroundColor: '#0064FF' } }}>
//                 {models.map((model, index) => (
//                     <Tab 
//                         key={model}
//                         label={model} 
//                         sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === index ? '#0064FF' : '#667085', backgroundColor: activeTab === index ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} 
//                     />
//                 ))}
//               </Tabs>
//               <Paper elevation={0} sx={{ p: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderRadius: '12px', bgcolor: 'white', width: '100%',ml:-2 }}>
//                   <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#232324', fontWeight: '400', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
//                   <Button id="trace-name-filter-button" aria-haspopup="true" variant="contained" onClick={handleTraceNameMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTraceNameMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#0064FF' : theme.palette.divider, backgroundColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{getTraceNameButtonText()}</Typography>
//                   </Button>
//                   <Menu id="trace-name-menu" anchorEl={traceNameAnchorEl} open={isTraceNameMenuOpen} onClose={handleTraceNameMenuClose} MenuListProps={{ 'aria-labelledby': 'trace-name-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Trace Name</Typography></Box> <Divider /> <Box sx={{ py: '10px' }}><FormGroup>{traceNames.map(name => (<FormControlLabel key={name} control={ <Checkbox checked={tempTraceNames.includes(name)} onChange={() => handleTraceNameCheckboxChange(name)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } label={ <Box sx={{ display: 'flex', alignItems: 'center' }}><Typography sx={{fontFamily:'Inter', fontSize:'14px'}}>{name}</Typography></Box> } sx={{ width: '100%', m: 0, px: '1px',pl:'6px', py: '4px', ...(tempTraceNames.includes(name) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTraceNames.includes(name) ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</FormGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: tempTraceNames.length > 0 ? '#333333' : '#7B7B7B', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//                   <Button id="time-range-filter-button" aria-haspopup="true" variant="contained" onClick={handleTimeRangeMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTimeRangeMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTimeRangeMenuOpen || selectedTimeRange ? '#0064FF' : theme.palette.divider, backgroundColor: isTimeRangeMenuOpen || selectedTimeRange ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{selectedTimeRange || 'Time Range'}</Typography>
//                   </Button>
//                   <Menu id="time-range-menu" anchorEl={timeRangeAnchorEl} open={isTimeRangeMenuOpen} onClose={handleTimeRangeMenuClose} MenuListProps={{ 'aria-labelledby': 'time-range-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Time Range</Typography></Box> <Divider /> <Box sx={{ py: '8px' }}><RadioGroup value={tempTimeRange} onChange={(e) => setTempTimeRange(e.target.value)}>{timeRanges.map(range => (<FormControlLabel key={range} value={range} control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={<Typography sx={{ fontFamily:'Inter', fontSize:'14px' }}>{range}</Typography>} sx={{ width: '100%', m: 0, px: '1px', py: '4px', ...(tempTimeRange === range && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTimeRange === range ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</RadioGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: !tempTimeRange ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//               </Paper>
//             </Box>
//             <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px',ml:-1 }}>
//               <Table sx={{ minWidth: 700 }} aria-label="traces table">
//                 <TableHead>
//                   <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', backgroundColor: 'white', py: 1.5, px: 3, fontFamily: 'Inter', fontSize: '12px', color: '#667085', fontWeight: 500 } }}>
//                     <TableCell>Name</TableCell><TableCell>Timestamp</TableCell><TableCell>Input</TableCell><TableCell>Output</TableCell><TableCell>Latency</TableCell><TableCell>Tokens</TableCell><TableCell>Cost</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {/* --- PAGINATION: Map over paginated data and handle empty state --- */}
//                   {paginatedTraces.length > 0 ? (
//                     paginatedTraces.map((trace, index) => (
//                       <TableRow key={trace.id} onClick={() => handleRowClick(trace)} sx={{ '& td, & th': { border: 0, py: 2.5, px: 3, fontFamily: 'Inter' }, backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'white', cursor: 'pointer', '&:hover': { backgroundColor: '#F0F2F5' }}}>
//                         <TableCell component="th" scope="row"><Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828' }}>{trace.name}</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{formatTimestamp(trace.timestamp)}</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.latency.toFixed(2)}s</Typography></TableCell>
//                         <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.totalTokens}</Typography><IconButton size="small" onMouseEnter={(e) => handleTokenPopoverOpen(e, trace)} onMouseLeave={handleTokenPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton></Box></TableCell>
//                         <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.cost !== null ? `$${trace.cost.toFixed(6)}` : '-'}</Typography>{trace.cost !== null && (<IconButton size="small" onMouseEnter={(e) => handleCostPopoverOpen(e, trace)} onMouseLeave={handleCostPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton>)}</Box></TableCell>
//                       </TableRow>
//                     ))
//                    ) : (
//                     <TableRow>
//                       <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, fontFamily: 'Inter', border: 0 }}>
//                           <Typography>No traces match the current filters.</Typography>
//                       </TableCell>
//                     </TableRow>
//                    )}
//                 </TableBody>
//               </Table>
//             </TableContainer>
            
//             {/* --- PAGINATION: Add the pagination controls UI --- */}
//             {filteredTraces.length > 0 && (
//                 <Box sx={{ flexShrink: 0, mt: 1, mb: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, minHeight: '3.5rem', padding: '0 1.5rem' }}>
//                     <Typography sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary' }}>Rows per page:</Typography>
//                     <FormControl size="small">
//                         <Select value={rowsPerPage} onChange={handleRowsPerPageChange} sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary', '.MuiOutlinedInput-notchedOutline': { border: 'none' }, '.MuiSelect-select': { padding: '4px 8px' }, }}>
//                             <MenuItem value={5}>5</MenuItem>
//                             <MenuItem value={10}>10</MenuItem>
//                             <MenuItem value={20}>20</MenuItem>
//                         </Select>
//                     </FormControl>
//                     <Typography sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
//                         {showingFrom} - {showingTo} of {filteredTraces.length}
//                     </Typography>
//                     <IconButton onClick={() => setPage(page - 1)} disabled={page === 1} aria-label="previous page">
//                         <ArrowBackIosNewIcon sx={{ fontSize: '1rem' }} />
//                     </IconButton>
//                     <IconButton onClick={() => setPage(page + 1)} disabled={page === pageCount} aria-label="next page">
//                         <ArrowForwardIosIcon sx={{ fontSize: '1rem' }} />
//                     </IconButton>
//                 </Box>
//             )}
//         </>
//     );
//   };

//   return (
//     <>
//       <Modal open={open} onClose={onClose} closeAfterTransition>
//         <Slide direction="up" in={open} mountOnEnter unmountOnExit>
//             <Box
//                 sx={{
//                 position: 'absolute',
//                 top: 60, 
//                 bottom: 15, 
//                 left: 15, 
//                 right: 15,
//                 bgcolor: 'background.paper',
//                 borderRadius: '24px',
//                 boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
//                 display: 'flex',
//                 flexDirection: 'column',
//                 overflow: 'hidden',
//                 border: '1px solid rgba(0, 0, 0, 0.1)',
//                 outline: 0,
//             }}>
//                 <Box sx={{
//                     pt: 0,
//                     px: '24px',
//                     pb: '24px',
//                     display: 'flex',
//                     justifyContent: 'space-between',
//                     alignItems: 'flex-start',
//                     borderBottom: '1px solid #EAECEE',
//                     backgroundImage: 'url(/textures/Texture.svg), url(/images/Gradient_Popup_long.svg)',
//                     backgroundRepeat: 'no-repeat, no-repeat',
//                     backgroundPosition: 'top right, top right',
//                     backgroundSize: 'contain, contain'
//                 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '24px' }}>
//                         <Box sx={{ width: 45, height: 45, bgcolor: ' #E5EFFF', borderRadius: '10%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                             <img src="/icons/llm-observability-blue.svg" alt="Traces Logo" style={{ width: 24, height: 24 }} />
//                         </Box>
//                         <Box>
//                             <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '18px',color:"#333333" }}>
//                                 Traces
//                             </Typography>
//                             <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Inter',fontSize: '12px',fontWeight: 400, }}>
//                                 Track deatils of every trace in here
//                             </Typography>
//                         </Box>
//                     </Box>
//                     <IconButton onClick={onClose} aria-label="close" sx={{ color: '#667085', mt: '16px' }}>
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>
//                 <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '24px', bgcolor: '#FFFFFF', position: 'relative' }}>
//                    {renderContent()}
//                 </Box>

//                 <TraceDetailPanel
//                   open={!!selectedTrace}
//                   onClose={handleDetailPanelClose}
//                   tenantId={tenantId}
//                   traceId={selectedTrace?.id}
//                 />
//             </Box>
//         </Slide>
//       </Modal>

//       {/* --- POPOVERS --- */}
//       <Popover open={isTokenPopoverOpen} anchorEl={tokenPopoverAnchorEl} onClose={handleTokenPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Token Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Input Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.input} </Typography> </Box> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Output Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.output} </Typography> </Box> </Box>
//       </Popover>

//       <Popover open={isCostPopoverOpen} anchorEl={costPopoverAnchorEl} onClose={handleCostPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Cost Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Total Cost Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> ${costPopoverData?.input.toFixed(6)} </Typography> </Box> </Box>
//       </Popover>
//     </>
//   );
// };

// export default TraceDetails;





// import React, { useMemo, useState, useEffect } from 'react'; // Added useEffect
// import {
//   Box,
//   Typography,
//   useTheme,
//   Button,
//   Tabs,
//   Tab,
//   TableContainer,
//   Paper,
//   Table,
//   TableHead,
//   TableRow,
//   TableCell,
//   TableBody,
//   InputAdornment,
//   Menu,
//   Divider,
//   TextField,
//   RadioGroup,
//   Radio,
//   FormControlLabel,
//   FormGroup,
//   Checkbox,
//   Popover,
//   IconButton,
//   CircularProgress,
//   Modal,
//   Slide,
//   // --- PAGINATION: Import required components ---
//   FormControl,
//   Select,
//   MenuItem,
//   SelectChangeEvent,
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import CloseIcon from '@mui/icons-material/Close';
// // --- PAGINATION: Import required icons ---
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// import { gql, useQuery } from '@apollo/client';
// import TraceDetailPanel from './TraceDetailPanel';


// // --- GQL QUERIES ---
// const GET_LLM_DATA_SOURCES = gql`
//   query GetLlmDataSourcesByTenant($tenantId: ID!) {
//     allDataSourcesByTenant(tenantId: $tenantId) {
//       __typename
//       ... on LlmDataSource {
//         id
//         providerType
//       }
//     }
//   }
// `;
// const GET_LLM_TRACES = gql`
//   query GetLlmTraces($tenantId: ID!, $llmDataSourceIds: [ID!]) {
//     getLlmTraces(
//       tenantId: $tenantId
//       llmDataSourceIds: $llmDataSourceIds
//     ) {
//       totalCount
//       traces {
//         id
//         timestamp
//         name
//         userId
//         latency
//         cost
//         totalTokens
//         inputTokens
//         outputTokens
//       }
//     }
//   }
// `;

// // --- INTERFACES ---
// interface TraceDetailsProps {
//   open: boolean;
//   onClose: () => void;
//   tenantId?: string;
// }
// interface Trace {
//   id: string;
//   timestamp: string;
//   name: string;
//   latency: number;
//   cost: number | null;
//   totalTokens: number;
//   inputTokens: number;
//   outputTokens: number;
// }

// const TraceDetails: React.FC<TraceDetailsProps> = ({ open, onClose, tenantId }) => {
//   const theme = useTheme();
//   const [activeTab, setActiveTab] = useState(0);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);

//   // --- PAGINATION: Add state for page number and rows per page ---
//   const [page, setPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(10);


//   const {
//     data: llmSourcesData,
//     loading: llmSourcesLoading,
//     error: llmSourcesError
//   } = useQuery(GET_LLM_DATA_SOURCES, {
//     variables: { tenantId: tenantId },
//     skip: !tenantId || !open,
//   });

//   const llmDataSourceIds = useMemo(() => {
//     if (!llmSourcesData?.allDataSourcesByTenant) return [];
//     return llmSourcesData.allDataSourcesByTenant
//       .filter((ds: any) => ds.__typename === 'LlmDataSource')
//       .map((ds: any) => ds.id);
//   }, [llmSourcesData]);

//   const { loading, error, data } = useQuery(GET_LLM_TRACES, {
//     variables: { tenantId: tenantId, llmDataSourceIds: llmDataSourceIds },
//     skip: !tenantId || llmDataSourceIds.length === 0 || !open,
//   });

//   const traces: Trace[] = useMemo(() => data?.getLlmTraces?.traces || [], [data]);
//   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);
//   const handleRowClick = (trace: Trace) => setSelectedTrace(trace);
//   const handleDetailPanelClose = () => setSelectedTrace(null);

//   const [tokenPopoverAnchorEl, setTokenPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [tokenPopoverData, setTokenPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleTokenPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setTokenPopoverData({ input: trace.inputTokens, output: trace.outputTokens }); setTokenPopoverAnchorEl(event.currentTarget); };
//   const handleTokenPopoverClose = () => setTokenPopoverAnchorEl(null);
//   const isTokenPopoverOpen = Boolean(tokenPopoverAnchorEl);

//   const [costPopoverAnchorEl, setCostPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [costPopoverData, setCostPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleCostPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setCostPopoverData({ input: trace.cost || 0, output: 0 }); setCostPopoverAnchorEl(event.currentTarget); };
//   const handleCostPopoverClose = () => setCostPopoverAnchorEl(null);
//   const isCostPopoverOpen = Boolean(costPopoverAnchorEl);

//   const [traceNameAnchorEl, setTraceNameAnchorEl] = useState<null | HTMLElement>(null);
//   const [timeRangeAnchorEl, setTimeRangeAnchorEl] = useState<null | HTMLElement>(null);
//   const isTraceNameMenuOpen = Boolean(traceNameAnchorEl);
//   const isTimeRangeMenuOpen = Boolean(timeRangeAnchorEl);
//   const [selectedTraceNames, setSelectedTraceNames] = useState<string[]>([]);
//   const [tempTraceNames, setTempTraceNames] = useState<string[]>([]);
//   const [selectedTimeRange, setSelectedTimeRange] = useState('');
//   const [tempTimeRange, setTempTimeRange] = useState('');

//   const traceNames = useMemo(() => {
//     if (!traces) return [];
//     const names = traces.map(trace => trace.name);
//     return Array.from(new Set(names));
//   }, [traces]);

//   const timeRanges = ['Last 24 Hours', 'Last 7 Days', 'Last 30 Days'];

//   const filteredTraces = useMemo(() => {
//     let result: Trace[] = [...traces];
//     if (selectedTraceNames.length > 0) {
//       result = result.filter(trace => selectedTraceNames.includes(trace.name));
//     }
//     if (selectedTimeRange) {
//       const now = new Date();
//       let cutoffDate: Date | null = null;
//       if (selectedTimeRange === 'Last 24 Hours') {
//         cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
//       } else if (selectedTimeRange === 'Last 7 Days') {
//         cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
//       } else if (selectedTimeRange === 'Last 30 Days') {
//         cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
//       }
//       if (cutoffDate) {
//         result = result.filter(trace => new Date(trace.timestamp) >= (cutoffDate as Date));
//       }
//     }
//     if (searchQuery) {
//       result = result.filter(trace =>
//         trace.name.toLowerCase().includes(searchQuery.toLowerCase())
//       );
//     }
//     return result;
//   }, [traces, selectedTraceNames, selectedTimeRange, searchQuery]);
  
//   // --- PAGINATION: Add effect to reset page when filters change ---
//   useEffect(() => {
//     setPage(1);
//   }, [searchQuery, selectedTraceNames, selectedTimeRange]);

//   // --- PAGINATION: Memoize the sliced data for the current page ---
//   const paginatedTraces = useMemo(() => {
//       const startIndex = (page - 1) * rowsPerPage;
//       return filteredTraces.slice(startIndex, startIndex + rowsPerPage);
//   }, [filteredTraces, page, rowsPerPage]);


//   const handleTraceNameMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTraceNames(selectedTraceNames); setTraceNameAnchorEl(event.currentTarget); };
//   const handleTraceNameMenuClose = () => setTraceNameAnchorEl(null);
//   const handleTraceNameCheckboxChange = (name: string) => { setTempTraceNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]); };
//   const handleApplyTraceNames = () => { setSelectedTraceNames(tempTraceNames); handleTraceNameMenuClose(); };
//   const handleClearTraceNames = () => { setTempTraceNames([]); setSelectedTraceNames([]); handleTraceNameMenuClose(); };
//   const getTraceNameButtonText = () => {
//     const selectedCount = selectedTraceNames.length;
//     const totalCount = traceNames.length;

//     if (selectedCount === 0) return 'Trace Name';
//     if (selectedCount === totalCount && totalCount > 0) return 'All';
//     return `${selectedCount} selected`; // This line now shows the count
// };

//   const handleTimeRangeMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTimeRange(selectedTimeRange); setTimeRangeAnchorEl(event.currentTarget); };
//   const handleTimeRangeMenuClose = () => setTimeRangeAnchorEl(null);
//   const handleApplyTimeRange = () => { setSelectedTimeRange(tempTimeRange); handleTimeRangeMenuClose(); };
//   const handleClearTimeRange = () => { setTempTimeRange(''); setSelectedTimeRange(''); handleTimeRangeMenuClose(); };

//   const formatTimestamp = (isoString: string) => { const date = new Date(isoString); const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }; const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true }; return `${date.toLocaleDateString('en-GB', dateOptions)} • ${date.toLocaleTimeString('en-US', timeOptions)}`; };
  
//   // --- PAGINATION: Handler for changing rows per page ---
//   const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
//       setRowsPerPage(event.target.value as number);
//       setPage(1);
//   };


//   const renderContent = () => {
//     if (llmSourcesLoading || (loading && llmDataSourceIds.length > 0)) {
//         return (
//           <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//             <CircularProgress />
//           </Box>
//         );
//     }
//     if (llmSourcesError) {
//       console.error("Error fetching LLM data sources:", JSON.stringify(llmSourcesError, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error: {llmSourcesError.message}</Typography>;
//     }
//     if (error) {
//       console.error("Error fetching LLM traces:", JSON.stringify(error, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error fetching traces: {error.message}</Typography>;
//     }
//     if (!tenantId) {
//         return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>Waiting for tenant information...</Typography>;
//     }
//     if (llmDataSourceIds.length === 0 && !llmSourcesLoading) {
//       return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>No LLM data sources found for this tenant.</Typography>;
//     }

//     // --- PAGINATION: Calculate values for the display text ---
//     const pageCount = Math.ceil(filteredTraces.length / rowsPerPage);
//     const showingFrom = filteredTraces.length > 0 ? (page - 1) * rowsPerPage + 1 : 0;
//     const showingTo = Math.min(page * rowsPerPage, filteredTraces.length);

//     return (
//         <>
//             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 3 }}>
//               <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 'auto', '& .MuiTabs-indicator': { backgroundColor: '#0064FF' } }}>
//                 <Tab label="gemini-2.5-pro" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 0 ? '#0064FF' : '#667085', backgroundColor: activeTab === 0 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//                 <Tab label="gemini-2.0-flash" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 1 ? '#0064FF' : '#667085', backgroundColor: activeTab === 1 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//                 <Tab label="gpt-4o" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 2 ? '#0064FF' : '#667085', backgroundColor: activeTab === 2 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//               </Tabs>
//               <Paper elevation={0} sx={{ p: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderRadius: '12px', bgcolor: 'white', width: '100%',ml:-2 }}>
//                   <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#232324', fontWeight: '400', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
//                   <Button id="trace-name-filter-button" aria-haspopup="true" variant="contained" onClick={handleTraceNameMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTraceNameMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#0064FF' : theme.palette.divider, backgroundColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{getTraceNameButtonText()}</Typography>
//                   </Button>
//                   <Menu id="trace-name-menu" anchorEl={traceNameAnchorEl} open={isTraceNameMenuOpen} onClose={handleTraceNameMenuClose} MenuListProps={{ 'aria-labelledby': 'trace-name-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Trace Name</Typography></Box> <Divider /> <Box sx={{ py: '10px' }}><FormGroup>{traceNames.map(name => (<FormControlLabel key={name} control={ <Checkbox checked={tempTraceNames.includes(name)} onChange={() => handleTraceNameCheckboxChange(name)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } label={ <Box sx={{ display: 'flex', alignItems: 'center' }}><Typography sx={{fontFamily:'Inter', fontSize:'14px'}}>{name}</Typography></Box> } sx={{ width: '100%', m: 0, px: '1px',pl:'6px', py: '4px', ...(tempTraceNames.includes(name) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTraceNames.includes(name) ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</FormGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: tempTraceNames.length > 0 ? '#333333' : '#7B7B7B', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//                   <Button id="time-range-filter-button" aria-haspopup="true" variant="contained" onClick={handleTimeRangeMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTimeRangeMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTimeRangeMenuOpen || selectedTimeRange ? '#0064FF' : theme.palette.divider, backgroundColor: isTimeRangeMenuOpen || selectedTimeRange ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{selectedTimeRange || 'Time Range'}</Typography>
//                   </Button>
//                   <Menu id="time-range-menu" anchorEl={timeRangeAnchorEl} open={isTimeRangeMenuOpen} onClose={handleTimeRangeMenuClose} MenuListProps={{ 'aria-labelledby': 'time-range-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Time Range</Typography></Box> <Divider /> <Box sx={{ py: '8px' }}><RadioGroup value={tempTimeRange} onChange={(e) => setTempTimeRange(e.target.value)}>{timeRanges.map(range => (<FormControlLabel key={range} value={range} control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={<Typography sx={{ fontFamily:'Inter', fontSize:'14px' }}>{range}</Typography>} sx={{ width: '100%', m: 0, px: '1px', py: '4px', ...(tempTimeRange === range && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTimeRange === range ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</RadioGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: !tempTimeRange ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//               </Paper>
//             </Box>
//             <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px',ml:-1 }}>
//               <Table sx={{ minWidth: 700 }} aria-label="traces table">
//                 <TableHead>
//                   <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', backgroundColor: 'white', py: 1.5, px: 3, fontFamily: 'Inter', fontSize: '12px', color: '#667085', fontWeight: 500 } }}>
//                     <TableCell>Name</TableCell><TableCell>Timestamp</TableCell><TableCell>Input</TableCell><TableCell>Output</TableCell><TableCell>Latency</TableCell><TableCell>Tokens</TableCell><TableCell>Cost</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {/* --- PAGINATION: Map over paginated data and handle empty state --- */}
//                   {paginatedTraces.length > 0 ? (
//                     paginatedTraces.map((trace, index) => (
//                       <TableRow key={trace.id} onClick={() => handleRowClick(trace)} sx={{ '& td, & th': { border: 0, py: 2.5, px: 3, fontFamily: 'Inter' }, backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'white', cursor: 'pointer', '&:hover': { backgroundColor: '#F0F2F5' }}}>
//                         <TableCell component="th" scope="row"><Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828' }}>{trace.name}</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{formatTimestamp(trace.timestamp)}</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                         <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.latency.toFixed(2)}s</Typography></TableCell>
//                         <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.totalTokens}</Typography><IconButton size="small" onMouseEnter={(e) => handleTokenPopoverOpen(e, trace)} onMouseLeave={handleTokenPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton></Box></TableCell>
//                         <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.cost !== null ? `$${trace.cost.toFixed(6)}` : '-'}</Typography>{trace.cost !== null && (<IconButton size="small" onMouseEnter={(e) => handleCostPopoverOpen(e, trace)} onMouseLeave={handleCostPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton>)}</Box></TableCell>
//                       </TableRow>
//                     ))
//                    ) : (
//                     <TableRow>
//                       <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, fontFamily: 'Inter', border: 0 }}>
//                           <Typography>No traces match the current filters.</Typography>
//                       </TableCell>
//                     </TableRow>
//                    )}
//                 </TableBody>
//               </Table>
//             </TableContainer>
            
//             {/* --- PAGINATION: Add the pagination controls UI --- */}
//             {filteredTraces.length > 0 && (
//                 <Box sx={{ flexShrink: 0, mt: 1, mb: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, minHeight: '3.5rem', padding: '0 1.5rem' }}>
//                     <Typography sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary' }}>Rows per page:</Typography>
//                     <FormControl size="small">
//                         <Select value={rowsPerPage} onChange={handleRowsPerPageChange} sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary', '.MuiOutlinedInput-notchedOutline': { border: 'none' }, '.MuiSelect-select': { padding: '4px 8px' }, }}>
//                             <MenuItem value={5}>5</MenuItem>
//                             <MenuItem value={10}>10</MenuItem>
//                             <MenuItem value={20}>20</MenuItem>
//                         </Select>
//                     </FormControl>
//                     <Typography sx={{ fontFamily: 'Inter', fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
//                         {showingFrom} - {showingTo} of {filteredTraces.length}
//                     </Typography>
//                     <IconButton onClick={() => setPage(page - 1)} disabled={page === 1} aria-label="previous page">
//                         <ArrowBackIosNewIcon sx={{ fontSize: '1rem' }} />
//                     </IconButton>
//                     <IconButton onClick={() => setPage(page + 1)} disabled={page === pageCount} aria-label="next page">
//                         <ArrowForwardIosIcon sx={{ fontSize: '1rem' }} />
//                     </IconButton>
//                 </Box>
//             )}
//         </>
//     );
//   };

//   return (
//     <>
//       <Modal open={open} onClose={onClose} closeAfterTransition>
//         <Slide direction="up" in={open} mountOnEnter unmountOnExit>
//             <Box
//                 sx={{
//                 position: 'absolute',
//                 top: 60, 
//                 bottom: 15, 
//                 left: 15, 
//                 right: 15,
//                 bgcolor: 'background.paper',
//                 borderRadius: '24px',
//                 boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
//                 display: 'flex',
//                 flexDirection: 'column',
//                 overflow: 'hidden',
//                 border: '1px solid rgba(0, 0, 0, 0.1)',
//                 outline: 0,
//             }}>
//                 <Box sx={{
//                     pt: 0,
//                     px: '24px',
//                     pb: '24px',
//                     display: 'flex',
//                     justifyContent: 'space-between',
//                     alignItems: 'flex-start',
//                     borderBottom: '1px solid #EAECEE',
//                     backgroundImage: 'url(/textures/Texture.svg), url(/images/Gradient_Popup_long.svg)',
//                     backgroundRepeat: 'no-repeat, no-repeat',
//                     backgroundPosition: 'top right, top right',
//                     backgroundSize: 'contain, contain'
//                 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '24px' }}>
//                         <Box sx={{ width: 45, height: 45, bgcolor: ' #E5EFFF', borderRadius: '10%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                             <img src="/icons/llm-observability-blue.svg" alt="Traces Logo" style={{ width: 24, height: 24 }} />
//                         </Box>
//                         <Box>
//                             <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '18px',color:"#333333" }}>
//                                 Traces
//                             </Typography>
//                             <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Inter',fontSize: '12px',fontWeight: 400, }}>
//                                 Track deatils of every trace in here
//                             </Typography>
//                         </Box>
//                     </Box>
//                     <IconButton onClick={onClose} aria-label="close" sx={{ color: '#667085', mt: '16px' }}>
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>
//                 <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '24px', bgcolor: '#FFFFFF', position: 'relative' }}>
//                    {renderContent()}
//                 </Box>

//                 <TraceDetailPanel
//                   open={!!selectedTrace}
//                   onClose={handleDetailPanelClose}
//                   tenantId={tenantId}
//                   traceId={selectedTrace?.id}
//                 />
//             </Box>
//         </Slide>
//       </Modal>

//       {/* --- POPOVERS --- */}
//       <Popover open={isTokenPopoverOpen} anchorEl={tokenPopoverAnchorEl} onClose={handleTokenPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Token Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Input Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.input} </Typography> </Box> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Output Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.output} </Typography> </Box> </Box>
//       </Popover>

//       <Popover open={isCostPopoverOpen} anchorEl={costPopoverAnchorEl} onClose={handleCostPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Cost Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Total Cost Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> ${costPopoverData?.input.toFixed(6)} </Typography> </Box> </Box>
//       </Popover>
//     </>
//   );
// };

// export default TraceDetails;




// import React, { useMemo, useState } from 'react';
// import {
//   Box,
//   Typography,
//   useTheme,
//   Button,
//   Tabs,
//   Tab,
//   TableContainer,
//   Paper,
//   Table,
//   TableHead,
//   TableRow,
//   TableCell,
//   TableBody,
//   InputAdornment,
//   Menu,
//   Divider,
//   TextField,
//   RadioGroup,
//   Radio,
//   FormControlLabel,
//   FormGroup,
//   Checkbox,
//   Popover,
//   IconButton,
//   CircularProgress,
//   Modal,
//   Slide,
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import CloseIcon from '@mui/icons-material/Close';
// import { gql, useQuery } from '@apollo/client';
// import TraceDetailPanel from './TraceDetailPanel';

// // --- GQL QUERIES ---
// const GET_LLM_DATA_SOURCES = gql`
//   query GetLlmDataSourcesByTenant($tenantId: ID!) {
//     allDataSourcesByTenant(tenantId: $tenantId) {
//       __typename
//       ... on LlmDataSource {
//         id
//         providerType
//       }
//     }
//   }
// `;
// const GET_LLM_TRACES = gql`
//   query GetLlmTraces($tenantId: ID!, $llmDataSourceIds: [ID!]) {
//     getLlmTraces(
//       tenantId: $tenantId
//       llmDataSourceIds: $llmDataSourceIds
//     ) {
//       totalCount
//       traces {
//         id
//         timestamp
//         name
//         userId
//         latency
//         cost
//         totalTokens
//         inputTokens
//         outputTokens
//       }
//     }
//   }
// `;

// // --- INTERFACES ---
// interface TraceDetailsProps {
//   open: boolean;
//   onClose: () => void;
//   tenantId?: string;
// }
// interface Trace {
//   id: string;
//   timestamp: string;
//   name: string;
//   latency: number;
//   cost: number | null;
//   totalTokens: number;
//   inputTokens: number;
//   outputTokens: number;
// }

// const TraceDetails: React.FC<TraceDetailsProps> = ({ open, onClose, tenantId }) => {
//   const theme = useTheme();
//   const [activeTab, setActiveTab] = useState(0);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);

//   const {
//     data: llmSourcesData,
//     loading: llmSourcesLoading,
//     error: llmSourcesError
//   } = useQuery(GET_LLM_DATA_SOURCES, {
//     variables: { tenantId: tenantId },
//     skip: !tenantId || !open,
//   });

//   const llmDataSourceIds = useMemo(() => {
//     if (!llmSourcesData?.allDataSourcesByTenant) return [];
//     return llmSourcesData.allDataSourcesByTenant
//       .filter((ds: any) => ds.__typename === 'LlmDataSource')
//       .map((ds: any) => ds.id);
//   }, [llmSourcesData]);

//   const { loading, error, data } = useQuery(GET_LLM_TRACES, {
//     variables: { tenantId: tenantId, llmDataSourceIds: llmDataSourceIds },
//     skip: !tenantId || llmDataSourceIds.length === 0 || !open,
//   });

//   const traces: Trace[] = useMemo(() => data?.getLlmTraces?.traces || [], [data]);
//   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);
//   const handleRowClick = (trace: Trace) => setSelectedTrace(trace);
//   const handleDetailPanelClose = () => setSelectedTrace(null);

//   const [tokenPopoverAnchorEl, setTokenPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [tokenPopoverData, setTokenPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleTokenPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setTokenPopoverData({ input: trace.inputTokens, output: trace.outputTokens }); setTokenPopoverAnchorEl(event.currentTarget); };
//   const handleTokenPopoverClose = () => setTokenPopoverAnchorEl(null);
//   const isTokenPopoverOpen = Boolean(tokenPopoverAnchorEl);

//   const [costPopoverAnchorEl, setCostPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [costPopoverData, setCostPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleCostPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setCostPopoverData({ input: trace.cost || 0, output: 0 }); setCostPopoverAnchorEl(event.currentTarget); };
//   const handleCostPopoverClose = () => setCostPopoverAnchorEl(null);
//   const isCostPopoverOpen = Boolean(costPopoverAnchorEl);

//   const [traceNameAnchorEl, setTraceNameAnchorEl] = useState<null | HTMLElement>(null);
//   const [timeRangeAnchorEl, setTimeRangeAnchorEl] = useState<null | HTMLElement>(null);
//   const isTraceNameMenuOpen = Boolean(traceNameAnchorEl);
//   const isTimeRangeMenuOpen = Boolean(timeRangeAnchorEl);
//   const [selectedTraceNames, setSelectedTraceNames] = useState<string[]>([]);
//   const [tempTraceNames, setTempTraceNames] = useState<string[]>([]);
//   const [selectedTimeRange, setSelectedTimeRange] = useState('');
//   const [tempTimeRange, setTempTimeRange] = useState('');

//   // --- MODIFIED: Dynamic trace names from API data ---
//   const traceNames = useMemo(() => {
//     if (!traces) return [];
//     const names = traces.map(trace => trace.name);
//     return Array.from(new Set(names));
//   }, [traces]);

//   const timeRanges = ['Last 24 Hours', 'Last 7 Days', 'Last 30 Days'];

//   // --- MODIFIED: Combined and functional filtering logic ---
//   const filteredTraces = useMemo(() => {
//     let result: Trace[] = [...traces];

//     // 1. Filter by selected trace names
//     if (selectedTraceNames.length > 0) {
//       result = result.filter(trace => selectedTraceNames.includes(trace.name));
//     }

//     // 2. Filter by selected time range
//     if (selectedTimeRange) {
//       const now = new Date();
//       let cutoffDate: Date | null = null;

//       if (selectedTimeRange === 'Last 24 Hours') {
//         cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
//       } else if (selectedTimeRange === 'Last 7 Days') {
//         cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
//       } else if (selectedTimeRange === 'Last 30 Days') {
//         cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
//       }

//       if (cutoffDate) {
//         result = result.filter(trace => new Date(trace.timestamp) >= (cutoffDate as Date));
//       }
//     }

//     // 3. Filter by search query
//     if (searchQuery) {
//       result = result.filter(trace =>
//         trace.name.toLowerCase().includes(searchQuery.toLowerCase())
//       );
//     }

//     return result;
//   }, [traces, selectedTraceNames, selectedTimeRange, searchQuery]);


//   const handleTraceNameMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTraceNames(selectedTraceNames); setTraceNameAnchorEl(event.currentTarget); };
//   const handleTraceNameMenuClose = () => setTraceNameAnchorEl(null);
//   const handleTraceNameCheckboxChange = (name: string) => { setTempTraceNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]); };
//   const handleApplyTraceNames = () => { setSelectedTraceNames(tempTraceNames); handleTraceNameMenuClose(); };
//   const handleClearTraceNames = () => { setTempTraceNames([]); setSelectedTraceNames([]); handleTraceNameMenuClose(); };
//   const getTraceNameButtonText = () => { if (selectedTraceNames.length === 0) return 'Trace Name'; if (selectedTraceNames.length === traceNames.length && traceNames.length > 0) return 'All'; return selectedTraceNames.join(' & '); };

//   const handleTimeRangeMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTimeRange(selectedTimeRange); setTimeRangeAnchorEl(event.currentTarget); };
//   const handleTimeRangeMenuClose = () => setTimeRangeAnchorEl(null);
//   const handleApplyTimeRange = () => { setSelectedTimeRange(tempTimeRange); handleTimeRangeMenuClose(); };
//   const handleClearTimeRange = () => { setTempTimeRange(''); setSelectedTimeRange(''); handleTimeRangeMenuClose(); };

//   const formatTimestamp = (isoString: string) => { const date = new Date(isoString); const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }; const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true }; return `${date.toLocaleDateString('en-GB', dateOptions)} • ${date.toLocaleTimeString('en-US', timeOptions)}`; };

//   const renderContent = () => {
//     if (llmSourcesLoading || (loading && llmDataSourceIds.length > 0)) {
//         return (
//           <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//             <CircularProgress />
//           </Box>
//         );
//     }
//     if (llmSourcesError) {
//       console.error("Error fetching LLM data sources:", JSON.stringify(llmSourcesError, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error: {llmSourcesError.message}</Typography>;
//     }
//     if (error) {
//       console.error("Error fetching LLM traces:", JSON.stringify(error, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error fetching traces: {error.message}</Typography>;
//     }
//     if (!tenantId) {
//         return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>Waiting for tenant information...</Typography>;
//     }
//     if (llmDataSourceIds.length === 0 && !llmSourcesLoading) {
//       return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>No LLM data sources found for this tenant.</Typography>;
//     }

//     return (
//         <>
//             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 3 }}>
//               <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 'auto', '& .MuiTabs-indicator': { backgroundColor: '#0064FF' } }}>
//                 <Tab label="gemini-2.5-pro" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 0 ? '#0064FF' : '#667085', backgroundColor: activeTab === 0 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//                 <Tab label="gemini-2.0-flash" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 1 ? '#0064FF' : '#667085', backgroundColor: activeTab === 1 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//                 <Tab label="Open AI" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 2 ? '#0064FF' : '#667085', backgroundColor: activeTab === 2 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//               </Tabs>
//               <Paper elevation={0} sx={{ p: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderRadius: '12px', bgcolor: 'white', width: '100%',ml:-2 }}>
//                   <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#232324', fontWeight: '400', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
//                   <Button id="trace-name-filter-button" aria-haspopup="true" variant="contained" onClick={handleTraceNameMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTraceNameMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#0064FF' : theme.palette.divider, backgroundColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{getTraceNameButtonText()}</Typography>
//                   </Button>
//                   <Menu id="trace-name-menu" anchorEl={traceNameAnchorEl} open={isTraceNameMenuOpen} onClose={handleTraceNameMenuClose} MenuListProps={{ 'aria-labelledby': 'trace-name-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Trace Name</Typography></Box> <Divider /> <Box sx={{ py: '10px' }}><FormGroup>{traceNames.map(name => (<FormControlLabel key={name} control={ <Checkbox checked={tempTraceNames.includes(name)} onChange={() => handleTraceNameCheckboxChange(name)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } label={ <Box sx={{ display: 'flex', alignItems: 'center' }}><Typography sx={{fontFamily:'Inter', fontSize:'14px'}}>{name}</Typography></Box> } sx={{ width: '100%', m: 0, px: '1px',pl:'6px', py: '4px', ...(tempTraceNames.includes(name) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTraceNames.includes(name) ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</FormGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: tempTraceNames.length > 0 ? '#333333' : '#7B7B7B', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//                   <Button id="time-range-filter-button" aria-haspopup="true" variant="contained" onClick={handleTimeRangeMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTimeRangeMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTimeRangeMenuOpen || selectedTimeRange ? '#0064FF' : theme.palette.divider, backgroundColor: isTimeRangeMenuOpen || selectedTimeRange ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{selectedTimeRange || 'Time Range'}</Typography>
//                   </Button>
//                   <Menu id="time-range-menu" anchorEl={timeRangeAnchorEl} open={isTimeRangeMenuOpen} onClose={handleTimeRangeMenuClose} MenuListProps={{ 'aria-labelledby': 'time-range-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Time Range</Typography></Box> <Divider /> <Box sx={{ py: '8px' }}><RadioGroup value={tempTimeRange} onChange={(e) => setTempTimeRange(e.target.value)}>{timeRanges.map(range => (<FormControlLabel key={range} value={range} control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={<Typography sx={{ fontFamily:'Inter', fontSize:'14px' }}>{range}</Typography>} sx={{ width: '100%', m: 0, px: '1px', py: '4px', ...(tempTimeRange === range && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTimeRange === range ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</RadioGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: !tempTimeRange ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//               </Paper>
//             </Box>
//             <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px',ml:-1 }}>
//               <Table sx={{ minWidth: 700 }} aria-label="traces table">
//                 <TableHead>
//                   <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', backgroundColor: 'white', py: 1.5, px: 3, fontFamily: 'Inter', fontSize: '12px', color: '#667085', fontWeight: 500 } }}>
//                     <TableCell>Name</TableCell><TableCell>Timestamp</TableCell><TableCell>Input</TableCell><TableCell>Output</TableCell><TableCell>Latency</TableCell><TableCell>Tokens</TableCell><TableCell>Cost</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {filteredTraces.map((trace, index) => (
//                     <TableRow key={trace.id} onClick={() => handleRowClick(trace)} sx={{ '& td, & th': { border: 0, py: 2.5, px: 3, fontFamily: 'Inter' }, backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'white', cursor: 'pointer', '&:hover': { backgroundColor: '#F0F2F5' }}}>
//                       <TableCell component="th" scope="row"><Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828' }}>{trace.name}</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{formatTimestamp(trace.timestamp)}</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.latency.toFixed(2)}s</Typography></TableCell>
//                       <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.totalTokens}</Typography><IconButton size="small" onMouseEnter={(e) => handleTokenPopoverOpen(e, trace)} onMouseLeave={handleTokenPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton></Box></TableCell>
//                       <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.cost !== null ? `$${trace.cost.toFixed(6)}` : '-'}</Typography>{trace.cost !== null && (<IconButton size="small" onMouseEnter={(e) => handleCostPopoverOpen(e, trace)} onMouseLeave={handleCostPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton>)}</Box></TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//         </>
//     );
//   };

//   return (
//     <>
//       <Modal open={open} onClose={onClose} closeAfterTransition>
//         <Slide direction="up" in={open} mountOnEnter unmountOnExit>
//             <Box
//                 sx={{
//                 position: 'absolute',
//                 top: 60, 
//                 bottom: 15, 
//                 left: 15, 
//                 right: 15,
//                 bgcolor: 'background.paper',
//                 borderRadius: '24px',
//                 boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
//                 display: 'flex',
//                 flexDirection: 'column',
//                 overflow: 'hidden',
//                 border: '1px solid rgba(0, 0, 0, 0.1)',
//                 outline: 0,
//             }}>
//                 <Box sx={{
//                     pt: 0,
//                     px: '24px',
//                     pb: '24px',
//                     display: 'flex',
//                     justifyContent: 'space-between',
//                     alignItems: 'flex-start',
//                     borderBottom: '1px solid #EAECEE',
//                     backgroundImage: 'url(/textures/Texture.svg), url(/images/Gradient_Popup_long.svg)',
//                     backgroundRepeat: 'no-repeat, no-repeat',
//                     backgroundPosition: 'top right, top right',
//                     backgroundSize: 'contain, contain'
//                 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '24px' }}>
//                         <Box sx={{ width: 45, height: 45, bgcolor: ' #E5EFFF', borderRadius: '10%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                             <img src="/icons/llm-observability-blue.svg" alt="Traces Logo" style={{ width: 24, height: 24 }} />
//                         </Box>
//                         <Box>
//                             <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '18px',color:"#333333" }}>
//                                 Traces
//                             </Typography>
//                             <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Inter',fontSize: '12px',fontWeight: 400, }}>
//                                 Track deatils of every trace in here
//                             </Typography>
//                         </Box>
//                     </Box>
//                     <IconButton onClick={onClose} aria-label="close" sx={{ color: '#667085', mt: '16px' }}>
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>
//                 <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '24px', bgcolor: '#FFFFFF', position: 'relative' }}>
//                    {renderContent()}
//                 </Box>

//                 <TraceDetailPanel
//                   open={!!selectedTrace}
//                   onClose={handleDetailPanelClose}
//                   tenantId={tenantId}
//                   traceId={selectedTrace?.id}
//                 />
//             </Box>
//         </Slide>
//       </Modal>

//       {/* --- POPOVERS --- */}
//       <Popover open={isTokenPopoverOpen} anchorEl={tokenPopoverAnchorEl} onClose={handleTokenPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Token Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Input Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.input} </Typography> </Box> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Output Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.output} </Typography> </Box> </Box>
//       </Popover>

//       <Popover open={isCostPopoverOpen} anchorEl={costPopoverAnchorEl} onClose={handleCostPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Cost Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Total Cost Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> ${costPopoverData?.input.toFixed(6)} </Typography> </Box> </Box>
//       </Popover>
//     </>
//   );
// };

// export default TraceDetails;



// import React, { useMemo, useState } from 'react';
// import {
//   Box,
//   Typography,
//   useTheme,
//   Button,
//   Tabs,
//   Tab,
//   TableContainer,
//   Paper,
//   Table,
//   TableHead,
//   TableRow,
//   TableCell,
//   TableBody,
//   InputAdornment,
//   Menu,
//   Divider,
//   TextField,
//   RadioGroup,
//   Radio,
//   FormControlLabel,
//   FormGroup,
//   Checkbox,
//   Popover,
//   IconButton,
//   CircularProgress,
//   Modal,
//   Slide,
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import CloseIcon from '@mui/icons-material/Close';
// import { gql, useQuery } from '@apollo/client';
// import TraceDetailPanel from './TraceDetailPanel';

// // --- GQL QUERIES ---
// const GET_LLM_DATA_SOURCES = gql`
//   query GetLlmDataSourcesByTenant($tenantId: ID!) {
//     allDataSourcesByTenant(tenantId: $tenantId) {
//       __typename
//       ... on LlmDataSource {
//         id
//         providerType
//       }
//     }
//   }
// `;
// const GET_LLM_TRACES = gql`
//   query GetLlmTraces($tenantId: ID!, $llmDataSourceIds: [ID!]) {
//     getLlmTraces(
//       tenantId: $tenantId
//       llmDataSourceIds: $llmDataSourceIds
//     ) {
//       totalCount
//       traces {
//         id
//         timestamp
//         name
//         userId
//         latency
//         cost
//         totalTokens
//         inputTokens
//         outputTokens
//       }
//     }
//   }
// `;

// // --- INTERFACES ---
// interface TraceDetailsProps {
//   open: boolean;
//   onClose: () => void;
//   tenantId?: string;
// }
// interface Trace {
//   id: string;
//   timestamp: string;
//   name: string;
//   latency: number;
//   cost: number | null;
//   totalTokens: number;
//   inputTokens: number;
//   outputTokens: number;
// }

// const TraceDetails: React.FC<TraceDetailsProps> = ({ open, onClose, tenantId }) => {
//   const theme = useTheme();
//   const [activeTab, setActiveTab] = useState(0);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);

//   const {
//     data: llmSourcesData,
//     loading: llmSourcesLoading,
//     error: llmSourcesError
//   } = useQuery(GET_LLM_DATA_SOURCES, {
//     variables: { tenantId: tenantId },
//     skip: !tenantId || !open,
//   });

//   const llmDataSourceIds = useMemo(() => {
//     if (!llmSourcesData?.allDataSourcesByTenant) return [];
//     return llmSourcesData.allDataSourcesByTenant
//       .filter((ds: any) => ds.__typename === 'LlmDataSource')
//       .map((ds: any) => ds.id);
//   }, [llmSourcesData]);

//   const { loading, error, data } = useQuery(GET_LLM_TRACES, {
//     variables: { tenantId: tenantId, llmDataSourceIds: llmDataSourceIds },
//     skip: !tenantId || llmDataSourceIds.length === 0 || !open,
//   });

//   const traces: Trace[] = useMemo(() => data?.getLlmTraces?.traces || [], [data]);
//   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);
//   const handleRowClick = (trace: Trace) => setSelectedTrace(trace);
//   const handleDetailPanelClose = () => setSelectedTrace(null);

//   const [tokenPopoverAnchorEl, setTokenPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [tokenPopoverData, setTokenPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleTokenPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setTokenPopoverData({ input: trace.inputTokens, output: trace.outputTokens }); setTokenPopoverAnchorEl(event.currentTarget); };
//   const handleTokenPopoverClose = () => setTokenPopoverAnchorEl(null);
//   const isTokenPopoverOpen = Boolean(tokenPopoverAnchorEl);

//   const [costPopoverAnchorEl, setCostPopoverAnchorEl] = useState<HTMLElement | null>(null);
//   const [costPopoverData, setCostPopoverData] = useState<{ input: number; output: number } | null>(null);
//   const handleCostPopoverOpen = (event: React.MouseEvent<HTMLElement>, trace: Trace) => { setCostPopoverData({ input: trace.cost || 0, output: 0 }); setCostPopoverAnchorEl(event.currentTarget); };
//   const handleCostPopoverClose = () => setCostPopoverAnchorEl(null);
//   const isCostPopoverOpen = Boolean(costPopoverAnchorEl);

//   const filteredTraces = traces.filter(trace => trace.name.toLowerCase().includes(searchQuery.toLowerCase()));
//   console.log("traces--->", traces);
//   console.log("filteredTraces--->", filteredTraces);
//   const [traceNameAnchorEl, setTraceNameAnchorEl] = useState<null | HTMLElement>(null);
//   const [timeRangeAnchorEl, setTimeRangeAnchorEl] = useState<null | HTMLElement>(null);
//   const isTraceNameMenuOpen = Boolean(traceNameAnchorEl);
//   const isTimeRangeMenuOpen = Boolean(timeRangeAnchorEl);
//   const [selectedTraceNames, setSelectedTraceNames] = useState<string[]>([]);
//   const [tempTraceNames, setTempTraceNames] = useState<string[]>([]);
//   const [selectedTimeRange, setSelectedTimeRange] = useState('');
//   const [tempTimeRange, setTempTimeRange] = useState('');

//   const traceNames = ['terminal-chat-turn', 'api-call', 'user-feedback'];
//   const timeRanges = ['Last 24 Hours', 'Last 7 Days', 'Last 30 Days'];

//   const handleTraceNameMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTraceNames(selectedTraceNames); setTraceNameAnchorEl(event.currentTarget); };
//   const handleTraceNameMenuClose = () => setTraceNameAnchorEl(null);
//   const handleTraceNameCheckboxChange = (name: string) => { setTempTraceNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]); };
//   const handleApplyTraceNames = () => { setSelectedTraceNames(tempTraceNames); handleTraceNameMenuClose(); };
//   const handleClearTraceNames = () => { setTempTraceNames([]); setSelectedTraceNames([]); handleTraceNameMenuClose(); };
//   const getTraceNameButtonText = () => { if (selectedTraceNames.length === 0) return 'Trace Name'; if (selectedTraceNames.length === traceNames.length) return 'All'; return selectedTraceNames.join(' & '); };

//   const handleTimeRangeMenuOpen = (event: React.MouseEvent<HTMLElement>) => { setTempTimeRange(selectedTimeRange); setTimeRangeAnchorEl(event.currentTarget); };
//   const handleTimeRangeMenuClose = () => setTimeRangeAnchorEl(null);
//   const handleApplyTimeRange = () => { setSelectedTimeRange(tempTimeRange); handleTimeRangeMenuClose(); };
//   const handleClearTimeRange = () => { setTempTimeRange(''); setSelectedTimeRange(''); handleTimeRangeMenuClose(); };

//   const formatTimestamp = (isoString: string) => { const date = new Date(isoString); const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }; const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true }; return `${date.toLocaleDateString('en-GB', dateOptions)} • ${date.toLocaleTimeString('en-US', timeOptions)}`; };

//   const renderContent = () => {
//     if (llmSourcesLoading || (loading && llmDataSourceIds.length > 0)) {
//         return (
//           <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//             <CircularProgress />
//           </Box>
//         );
//     }
//     if (llmSourcesError) {
//       console.error("Error fetching LLM data sources:", JSON.stringify(llmSourcesError, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error: {llmSourcesError.message}</Typography>;
//     }
//     if (error) {
//       console.error("Error fetching LLM traces:", JSON.stringify(error, null, 2));
//       return <Typography color="error" sx={{ p: 3, fontFamily: 'Inter' }}>Error fetching traces: {error.message}</Typography>;
//     }
//     if (!tenantId) {
//         return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>Waiting for tenant information...</Typography>;
//     }
//     if (llmDataSourceIds.length === 0 && !llmSourcesLoading) {
//       return <Typography sx={{ p: 3, textAlign: 'center', fontFamily: 'Inter' }}>No LLM data sources found for this tenant.</Typography>;
//     }

//     return (
//         <>
//             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 3 }}>
//               <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 'auto', '& .MuiTabs-indicator': { backgroundColor: '#0064FF' } }}>
//                 <Tab label="gemini-2.5-pro" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 0 ? '#0064FF' : '#667085', backgroundColor: activeTab === 0 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//                 <Tab label="gemini-2.0-flash" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 1 ? '#0064FF' : '#667085', backgroundColor: activeTab === 1 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//                 <Tab label="Open AI" sx={{ textTransform: 'none', minHeight: 'auto', fontWeight: 500, fontSize: '14px', borderRadius: '8px', padding: '6px 16px', color: activeTab === 2 ? '#0064FF' : '#667085', backgroundColor: activeTab === 2 ? '#E9F2FF' : 'transparent', '&.Mui-selected': { color: '#0064FF' } }} />
//               </Tabs>
//               <Paper elevation={0} sx={{ p: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderRadius: '12px', bgcolor: 'white', width: '100%',ml:-2 }}>
//                   <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#999999', fontWeight: '300', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
//                   <Button id="trace-name-filter-button" aria-haspopup="true" variant="contained" onClick={handleTraceNameMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTraceNameMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#0064FF' : theme.palette.divider, backgroundColor: isTraceNameMenuOpen || selectedTraceNames.length > 0 ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{getTraceNameButtonText()}</Typography>
//                   </Button>
//                   <Menu id="trace-name-menu" anchorEl={traceNameAnchorEl} open={isTraceNameMenuOpen} onClose={handleTraceNameMenuClose} MenuListProps={{ 'aria-labelledby': 'trace-name-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Trace Name</Typography></Box> <Divider /> <Box sx={{ py: '10px' }}><FormGroup>{traceNames.map(name => (<FormControlLabel key={name} control={ <Checkbox checked={tempTraceNames.includes(name)} onChange={() => handleTraceNameCheckboxChange(name)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> } label={ <Box sx={{ display: 'flex', alignItems: 'center' }}><Typography sx={{fontFamily:'Inter', fontSize:'14px'}}>{name}</Typography></Box> } sx={{ width: '100%', m: 0, px: '1px',pl:'6px', py: '4px', ...(tempTraceNames.includes(name) && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTraceNames.includes(name) ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</FormGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: tempTraceNames.length > 0 ? '#333333' : '#7B7B7B', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTraceNames} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//                   <Button id="time-range-filter-button" aria-haspopup="true" variant="contained" onClick={handleTimeRangeMenuOpen} endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isTimeRangeMenuOpen ? 'rotate(180deg)' : 'none' }} />} sx={{ textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px', borderRadius: '8px', boxShadow: 'none', border: '1px solid', borderColor: isTimeRangeMenuOpen || selectedTimeRange ? '#0064FF' : theme.palette.divider, backgroundColor: isTimeRangeMenuOpen || selectedTimeRange ? '#F3F8FE' : 'transparent', '&:hover': { boxShadow: 'none', borderColor: '#0064FF' }}}>
//                       <Typography component="span" sx={{ fontSize: '14px', fontFamily: 'Inter' }}>{selectedTimeRange || 'Time Range'}</Typography>
//                   </Button>
//                   <Menu id="time-range-menu" anchorEl={timeRangeAnchorEl} open={isTimeRangeMenuOpen} onClose={handleTimeRangeMenuClose} MenuListProps={{ 'aria-labelledby': 'time-range-filter-button', sx: { p: 0 } }} PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }} >
//                     <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>Select Time Range</Typography></Box> <Divider /> <Box sx={{ py: '8px' }}><RadioGroup value={tempTimeRange} onChange={(e) => setTempTimeRange(e.target.value)}>{timeRanges.map(range => (<FormControlLabel key={range} value={range} control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />} label={<Typography sx={{ fontFamily:'Inter', fontSize:'14px' }}>{range}</Typography>} sx={{ width: '100%', m: 0, px: '1px', py: '4px', ...(tempTimeRange === range && { backgroundColor: '#F3F8FE' }) }} componentsProps={{ typography: { sx: { color: tempTimeRange === range ? '#333333' : '#666666', fontSize: '14px' } } }}/>))}</RadioGroup></Box> <Divider /> <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}><Button onClick={handleClearTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: !tempTimeRange ? '#7B7B7B' : '#333333', minWidth: 'auto', padding: '6px 8px' }}>Clear</Button><Button onClick={handleApplyTimeRange} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>Apply</Button></Box>
//                   </Menu>
//               </Paper>
//             </Box>
//             <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px',ml:-1 }}>
//               <Table sx={{ minWidth: 700 }} aria-label="traces table">
//                 <TableHead>
//                   <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', backgroundColor: 'white', py: 1.5, px: 3, fontFamily: 'Inter', fontSize: '12px', color: '#667085', fontWeight: 500 } }}>
//                     <TableCell>Name</TableCell><TableCell>Timestamp</TableCell><TableCell>Input</TableCell><TableCell>Output</TableCell><TableCell>Latency</TableCell><TableCell>Tokens</TableCell><TableCell>Cost</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {filteredTraces.map((trace, index) => (
//                     <TableRow key={trace.id} onClick={() => handleRowClick(trace)} sx={{ '& td, & th': { border: 0, py: 2.5, px: 3, fontFamily: 'Inter' }, backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'white', cursor: 'pointer', '&:hover': { backgroundColor: '#F0F2F5' }}}>
//                       <TableCell component="th" scope="row"><Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828' }}>{trace.name}</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{formatTimestamp(trace.timestamp)}</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>-</Typography></TableCell>
//                       <TableCell><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.latency.toFixed(2)}s</Typography></TableCell>
//                       <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.totalTokens}</Typography><IconButton size="small" onMouseEnter={(e) => handleTokenPopoverOpen(e, trace)} onMouseLeave={handleTokenPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton></Box></TableCell>
//                       <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Typography sx={{ fontSize: '14px', color: '#667085' }}>{trace.cost !== null ? `$${trace.cost.toFixed(6)}` : '-'}</Typography>{trace.cost !== null && (<IconButton size="small" onMouseEnter={(e) => handleCostPopoverOpen(e, trace)} onMouseLeave={handleCostPopoverClose} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: '#0064FF' }} /></IconButton>)}</Box></TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//         </>
//     );
//   };

//   return (
//     <>
//       <Modal open={open} onClose={onClose} closeAfterTransition>
//         <Slide direction="up" in={open} mountOnEnter unmountOnExit>
//             <Box
//                 sx={{
//                 position: 'absolute',
//                 top: 60, 
//                 bottom: 15, 
//                 left: 15, 
//                 right: 15,
//                 bgcolor: 'background.paper',
//                 borderRadius: '24px',
//                 boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
//                 display: 'flex',
//                 flexDirection: 'column',
//                 overflow: 'hidden',
//                 border: '1px solid rgba(0, 0, 0, 0.1)',
//                 outline: 0,
//             }}>
//                 <Box sx={{
//                     // --- UPDATED STYLES ---
//                     pt: 0,
//                     px: '24px',
//                     pb: '24px',
//                     display: 'flex',
//                     justifyContent: 'space-between',
//                     alignItems: 'flex-start',
//                     borderBottom: '1px solid #EAECEE',
                    
//                     // Layered background images. The first image is the top layer.
//                     backgroundImage: 'url(/textures/Texture.svg), url(/images/Gradient_Popup_long.svg)',
                    
//                     // Define properties for each background layer respectively.
//                     backgroundRepeat: 'no-repeat, no-repeat',
//                     backgroundPosition: 'top right, top right',
//                     backgroundSize: 'contain, contain'
//                 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '24px' /* Added margin-top to content to compensate for removed padding */ }}>
//                         <Box sx={{ width: 45, height: 45, bgcolor: ' #E5EFFF', borderRadius: '10%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                             <img src="/icons/llm-observability-blue.svg" alt="Traces Logo" style={{ width: 24, height: 24 }} />
//                         </Box>
//                         <Box>
//                             <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '18px',color:"#333333" }}>
//                                 Traces
//                             </Typography>
//                             <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Inter',fontSize: '12px',fontWeight: 400, }}>
//                                 Track deatils of every trace in here
//                             </Typography>
//                         </Box>
//                     </Box>
//                     <IconButton onClick={onClose} aria-label="close" sx={{ color: '#667085', mt: '16px' /* Added margin-top to align with content */ }}>
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>
//                 <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '24px', bgcolor: '#FFFFFF', position: 'relative' }}>
//                    {renderContent()}
//                 </Box>

//                 {/* --- CORRECTED PROPS --- */}
//                 <TraceDetailPanel
//                   open={!!selectedTrace}
//                   onClose={handleDetailPanelClose}
//                   tenantId={tenantId}
//                   traceId={selectedTrace?.id}
//                 />
//             </Box>
//         </Slide>
//       </Modal>

//       {/* --- POPOVERS --- */}
//       <Popover open={isTokenPopoverOpen} anchorEl={tokenPopoverAnchorEl} onClose={handleTokenPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Token Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Input Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.input} </Typography> </Box> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Output Token Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> {tokenPopoverData?.output} </Typography> </Box> </Box>
//       </Popover>

//       <Popover open={isCostPopoverOpen} anchorEl={costPopoverAnchorEl} onClose={handleCostPopoverClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} PaperProps={{ elevation: 3, sx: { p: 2, borderRadius: '12px', mt: 1 } }} sx={{ pointerEvents: 'none' }}>
//         <Box> <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '14px', color: '#101828' }}>Cost Usage Breakdown</Typography> <Box sx={{ border: '1px solid #D0D5DD', borderRadius: '20px', px: 1.5, py: 0.5, mb: 1 }}> <Typography component="span" sx={{ fontSize: '12px', color: '#344054' }}> Total Cost Usage:&nbsp; </Typography> <Typography component="span" sx={{ fontSize: '12px', color: '#344054', fontWeight: 500 }}> ${costPopoverData?.input.toFixed(6)} </Typography> </Box> </Box>
//       </Popover>
//     </>
//   );
// };

// export default TraceDetails;